import { yupResolver } from '@mantine/form';
import * as Yup from 'yup';
import {
    SapDivisionOption,
    SapStoreOption,
    VirtualDivisionFormValues,
    VirtualDivisionInputPayload,
    VirtualDivisionRow,
    VirtualHierarchyUser,
} from './types';

type NamedStore = {
    storeNo: string;
    name?: string | null;
};

export function formatUserLabel(user: VirtualHierarchyUser): string {
    const name = `${user.firstName} ${user.lastName}`.trim();
    if (user.designation) {
        return `${name} (${user.designation})`;
    }
    return name;
}

export function formatStoreLabel(store: NamedStore): string {
    const storeName = store.name?.trim();
    if (storeName) {
        return `${store.storeNo} ${storeName}`;
    }
    return store.storeNo;
}

export const emptyVirtualDivisionValues: VirtualDivisionFormValues = {
    name: '',
    managerUserId: '',
    sourceDivisionId: '',
    storeIds: [],
    grouping: 'sap',
    sapOpsAreaId: '',
    virtualOpsAreaId: '',
    newVirtualOpsAreaName: '',
    newVirtualOpsAreaExecutiveUserId: '',
};

export function toVirtualDivisionFormValues(
    division?: VirtualDivisionRow,
): VirtualDivisionFormValues {
    if (!division) {
        return {
            ...emptyVirtualDivisionValues,
            storeIds: [],
        };
    }

    return {
        name: division.name,
        managerUserId: division.manager?.id ?? '',
        sourceDivisionId: '',
        storeIds: division.stores.map(store => store.id),
        grouping: division.virtualOpsArea ? 'existing' : 'sap',
        sapOpsAreaId: division.sapOpsAreaId ?? '',
        virtualOpsAreaId: division.virtualOpsArea?.id ?? '',
        newVirtualOpsAreaName: '',
        newVirtualOpsAreaExecutiveUserId: '',
    };
}

export function toVirtualDivisionPayload(
    values: VirtualDivisionFormValues,
): VirtualDivisionInputPayload {
    const payload: VirtualDivisionInputPayload = {
        name: values.name.trim(),
        managerUserId: values.managerUserId,
        storeIds: values.storeIds,
    };

    if (values.sourceDivisionId) {
        payload.sourceDivisionId = values.sourceDivisionId;
    }

    if (values.grouping === 'sap' && values.sapOpsAreaId) {
        payload.sapOpsAreaId = values.sapOpsAreaId;
    }

    if (values.grouping === 'existing' && values.virtualOpsAreaId) {
        payload.virtualOpsAreaId = values.virtualOpsAreaId;
    }

    if (values.grouping === 'new') {
        payload.newVirtualOpsAreaName = values.newVirtualOpsAreaName.trim();
        payload.newVirtualOpsAreaExecutiveUserId =
            values.newVirtualOpsAreaExecutiveUserId;
    }

    return payload;
}

export function getOccupiedStoreIds(
    divisions: VirtualDivisionRow[],
    editingDivisionId?: string,
): Set<string> {
    const occupied = new Set<string>();
    divisions.forEach(division => {
        if (editingDivisionId && division.id === editingDivisionId) {
            return;
        }
        division.stores.forEach(store => occupied.add(store.id));
    });
    return occupied;
}

export function getStoresForVirtualAssignment(
    sapDivisions: SapDivisionOption[],
    sourceDivisionId: string,
    selectedStoreIds: string[],
): SapStoreOption[] {
    const storesById = new Map<string, SapStoreOption>();
    sapDivisions.forEach(division => {
        const matchesFilter =
            !sourceDivisionId || division.id === sourceDivisionId;
        division.stores.forEach(store => {
            if (matchesFilter || selectedStoreIds.includes(store.id)) {
                if (!storesById.has(store.id)) {
                    storesById.set(store.id, store);
                }
            }
        });
    });
    return [...storesById.values()].sort((left, right) =>
        left.storeNo.localeCompare(right.storeNo),
    );
}

export function getSapOpsAreaOptions(
    sapDivisions: SapDivisionOption[],
): Array<{ value: string; label: string }> {
    const namesById = new Map<string, string>();
    sapDivisions.forEach(division => {
        if (division.opsArea) {
            namesById.set(division.opsArea.id, division.opsArea.name);
        }
    });
    return [...namesById.entries()]
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((left, right) => left.label.localeCompare(right.label));
}

const divisionStepSchema = Yup.object().shape({
    name: Yup.string().trim().required('Enter a virtual division name'),
    managerUserId: Yup.string().required('Select a virtual divisional manager'),
});

const storesStepSchema = Yup.object().shape({
    storeIds: Yup.array()
        .of(Yup.string().required())
        .min(1, 'Select at least one store'),
});

const groupingStepSchema = Yup.object().shape({
    sapOpsAreaId: Yup.string().when('grouping', {
        is: 'sap',
        then: schema => schema.required('Select a SAP ops area'),
        otherwise: schema => schema.notRequired(),
    }),
    virtualOpsAreaId: Yup.string().when('grouping', {
        is: 'existing',
        then: schema => schema.required('Select a virtual ops area'),
        otherwise: schema => schema.notRequired(),
    }),
    newVirtualOpsAreaName: Yup.string().when('grouping', {
        is: 'new',
        then: schema => schema.trim().required('Enter a virtual ops area name'),
        otherwise: schema => schema.notRequired(),
    }),
    newVirtualOpsAreaExecutiveUserId: Yup.string().when('grouping', {
        is: 'new',
        then: schema => schema.required('Select a virtual ops executive'),
        otherwise: schema => schema.notRequired(),
    }),
});

function getVirtualHierarchyStepSchema(step: number) {
    if (step === 0) {
        return divisionStepSchema;
    }
    if (step === 1) {
        return storesStepSchema;
    }
    return groupingStepSchema;
}

export function getVirtualHierarchyStepErrors(
    step: number,
    values: VirtualDivisionFormValues,
): Record<string, string> {
    const resolved = yupResolver(getVirtualHierarchyStepSchema(step))(values);
    const errors: Record<string, string> = {};
    Object.entries(resolved).forEach(([field, message]) => {
        if (typeof message === 'string') {
            errors[field] = message;
        }
    });
    return errors;
}
